import { Controller } from "@hotwired/stimulus"
import { loadChart } from "../lib/load_chart"

export default class extends Controller {
  static values = {
    labels: Array,
    datasets: Object,
    translations: Object,
    chartType: { type: String, default: 'default' }
  }

  async connect() {
    this.Chart = await loadChart()
    if (this.element.isConnected) this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    const chartType = this.chartTypeValue

    switch (chartType) {
      case 'user_growth':
        this.renderUserGrowthChart()
        break
      case 'revenue':
        this.renderRevenueChart()
        break
      case 'conversion_rate':
        this.renderConversionRateChart()
        break
      case 'activity':
        this.renderActivityChart()
        break
      case 'guest_funnel':
        this.renderGuestFunnelChart()
        break
      default:
        this.renderDefaultChart()
    }
  }

  getDataForMonthKeys(dataSource) {
    const monthKeys = this.datasetsValue.month_keys || []
    return monthKeys.map(monthKey => dataSource[monthKey] || 0)
  }

  // Builds a two-point forecast series: [null, ..., prevActual, forecastValue].
  // Returns null if the forecast payload is missing or if the current month is
  // not in the displayed range.
  buildForecastSeries(actualData, metricKey) {
    const forecastObj = this.datasetsValue.forecast
    if (!forecastObj || forecastObj[metricKey] == null) return null

    const monthKeys = this.datasetsValue.month_keys || []
    const currentKey = forecastObj.current_month_key
    const currentIdx = monthKeys.indexOf(currentKey)
    if (currentIdx < 0) return null

    const series = new Array(monthKeys.length).fill(null)
    series[currentIdx] = forecastObj[metricKey]
    if (currentIdx > 0) {
      const prevKey = monthKeys[currentIdx - 1]
      series[currentIdx - 1] = actualData[prevKey] || 0
    }
    return series
  }

  forecastDatasetOptions(label, color, extra = {}) {
    return {
      label: label,
      borderColor: color,
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [5, 5],
      pointRadius: 4,
      pointHoverRadius: 6,
      pointStyle: 'rectRot',
      tension: 0.3,
      fill: false,
      type: 'line',
      spanGaps: false,
      ...extra
    }
  }

  forecastLabel(translations, metricTranslation) {
    const prefix = translations.forecast || 'Forecast'
    return metricTranslation ? `${prefix} · ${metricTranslation}` : prefix
  }

  renderUserGrowthChart() {
    const ctx = this.element
    const translations = this.translationsValue || {}
    const registrationsData = this.datasetsValue.registrations || {}

    const datasets = [
      {
        label: translations.registrations || 'Registrations',
        data: this.getDataForMonthKeys(registrationsData),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]

    const forecastRegistrations = this.buildForecastSeries(registrationsData, 'registrations')
    if (forecastRegistrations) {
      datasets.push({
        ...this.forecastDatasetOptions(
          this.forecastLabel(translations, translations.registrations || 'Registrations'),
          'rgb(59, 130, 246)'
        ),
        data: forecastRegistrations
      })
    }

    this.chart = new this.Chart(ctx, {
      type: 'line',
      data: {
        labels: this.labelsValue,
        datasets: datasets
      },
      options: this.getLineChartOptions()
    })
  }

  renderGuestFunnelChart() {
    const ctx = this.element
    const translations = this.translationsValue || {}
    const attemptsData = this.datasetsValue.guest_attempts || {}
    const conversionsData = this.datasetsValue.guest_conversions || {}

    const datasets = [
      {
        label: translations.guest_attempts || 'Guest Attempts',
        data: this.getDataForMonthKeys(attemptsData),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6
      },
      {
        label: translations.guest_conversions || 'Guest Conversions',
        data: this.getDataForMonthKeys(conversionsData),
        borderColor: 'rgb(32, 201, 151)',
        backgroundColor: 'rgba(32, 201, 151, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]

    const forecastAttempts = this.buildForecastSeries(attemptsData, 'guest_attempts')
    if (forecastAttempts) {
      datasets.push({
        ...this.forecastDatasetOptions(
          this.forecastLabel(translations, translations.guest_attempts || 'Guest Attempts'),
          'rgb(245, 158, 11)'
        ),
        data: forecastAttempts
      })
    }

    this.chart = new this.Chart(ctx, {
      type: 'line',
      data: {
        labels: this.labelsValue,
        datasets: datasets
      },
      options: this.getLineChartOptions()
    })
  }

  renderRevenueChart() {
    const ctx = this.element
    const translations = this.translationsValue || {}
    const revenueData = this.datasetsValue.revenue || {}
    const upgradesData = this.datasetsValue.upgrades || {}

    const datasets = [
      {
        label: translations.revenue || 'Revenue (GEL)',
        data: this.getDataForMonthKeys(revenueData),
        backgroundColor: 'rgba(32, 201, 151, 0.8)',
        borderColor: 'rgb(32, 201, 151)',
        borderWidth: 1,
        yAxisID: 'y'
      },
      {
        type: 'line',
        label: translations.upgrades || 'Pro Upgrades',
        data: this.getDataForMonthKeys(upgradesData),
        borderColor: 'rgb(98, 125, 152)',
        backgroundColor: 'rgba(98, 125, 152, 0.1)',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y1'
      }
    ]

    const forecastRevenue = this.buildForecastSeries(revenueData, 'revenue')
    if (forecastRevenue) {
      datasets.push({
        ...this.forecastDatasetOptions(
          this.forecastLabel(translations, translations.revenue || 'Revenue (GEL)'),
          'rgb(32, 201, 151)',
          { yAxisID: 'y' }
        ),
        data: forecastRevenue
      })
    }

    const forecastUpgrades = this.buildForecastSeries(upgradesData, 'upgrades')
    if (forecastUpgrades) {
      datasets.push({
        ...this.forecastDatasetOptions(
          this.forecastLabel(translations, translations.upgrades || 'Pro Upgrades'),
          'rgb(98, 125, 152)',
          { yAxisID: 'y1' }
        ),
        data: forecastUpgrades
      })
    }

    this.chart = new this.Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.labelsValue,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.parsed.y !== null) {
                  if (context.dataset.yAxisID === 'y') {
                    label += 'GEL ' + context.parsed.y.toLocaleString()
                  } else {
                    label += context.parsed.y.toLocaleString()
                  }
                }
                return label
              }
            }
          }
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return 'GEL ' + value.toLocaleString()
              }
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            beginAtZero: true,
            grid: {
              drawOnChartArea: false
            },
            ticks: {
              callback: function(value) {
                return value.toLocaleString()
              }
            }
          }
        }
      }
    })
  }

  renderConversionRateChart() {
    const ctx = this.element
    const translations = this.translationsValue || {}
    const upgradeRateData = this.datasetsValue.upgrade_rate || {}
    const limitReachedRateData = this.datasetsValue.limit_reached_rate || {}

    const datasets = [
      {
        label: translations.upgrade_rate || 'Upgrade Rate',
        data: this.getDataForMonthKeys(upgradeRateData),
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderDash: [5, 5]
      },
      {
        label: translations.limit_reached_rate || 'Limit Reached Rate',
        data: this.getDataForMonthKeys(limitReachedRateData),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]

    this.chart = new this.Chart(ctx, {
      type: 'line',
      data: {
        labels: this.labelsValue,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y.toFixed(1) + '%'
                }
                return label
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return value + '%'
              }
            }
          }
        }
      }
    })
  }

  renderActivityChart() {
    const ctx = this.element
    const translations = this.translationsValue || {}
    const freeQuestionsData = this.datasetsValue.free_questions || {}
    const proQuestionsData = this.datasetsValue.pro_questions || {}

    const datasets = [
      {
        label: translations.free_questions || 'Free Users Questions',
        data: this.getDataForMonthKeys(freeQuestionsData),
        backgroundColor: 'rgba(6, 182, 212, 0.8)',
        borderColor: 'rgb(6, 182, 212)',
        borderWidth: 1
      },
      {
        label: translations.pro_questions || 'Pro Users Questions',
        data: this.getDataForMonthKeys(proQuestionsData),
        backgroundColor: 'rgba(32, 201, 151, 0.8)',
        borderColor: 'rgb(32, 201, 151)',
        borderWidth: 1
      }
    ]

    // Stacked bar chart: show a single total forecast line above the stack
    // rather than two overlapping forecasts that would clash with the stacking.
    const forecastFreeSeries = this.buildForecastSeries(freeQuestionsData, 'free_questions')
    const forecastProSeries = this.buildForecastSeries(proQuestionsData, 'pro_questions')
    if (forecastFreeSeries && forecastProSeries) {
      const totalForecastSeries = forecastFreeSeries.map((val, idx) => {
        const other = forecastProSeries[idx]
        if (val == null && other == null) return null
        return (val || 0) + (other || 0)
      })
      datasets.push({
        ...this.forecastDatasetOptions(
          this.forecastLabel(translations, translations.questions_answered || 'Questions Answered'),
          'rgb(32, 201, 151)'
        ),
        data: totalForecastSeries
      })
    }

    this.chart = new this.Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.labelsValue,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y.toLocaleString()
                }
                return label
              }
            }
          }
        },
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return value.toLocaleString()
              }
            }
          }
        }
      }
    })
  }

  renderDefaultChart() {
    const ctx = this.element
    const translations = this.translationsValue || {}
    const registrationsData = this.datasetsValue.registrations || {}
    const activeUsersData = this.datasetsValue.active_users || {}
    const questionsData = this.datasetsValue.questions || {}

    const datasets = [
      {
        label: translations.registrations || 'Registrations',
        data: this.getDataForMonthKeys(registrationsData),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y'
      },
      {
        label: translations.active_users || 'Active Users',
        data: this.getDataForMonthKeys(activeUsersData),
        borderColor: 'rgb(236, 72, 153)',
        backgroundColor: 'rgba(236, 72, 153, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y'
      },
      {
        label: translations.questions_answered || 'Questions Answered',
        data: this.getDataForMonthKeys(questionsData),
        borderColor: 'rgb(6, 182, 212)',
        backgroundColor: 'rgba(6, 182, 212, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y1'
      }
    ]

    this.chart = new this.Chart(ctx, {
      type: 'line',
      data: {
        labels: this.labelsValue,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y.toLocaleString()
                }
                return label
              }
            }
          }
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return value.toLocaleString()
              }
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            beginAtZero: true,
            grid: {
              drawOnChartArea: false
            },
            ticks: {
              callback: function(value) {
                return value.toLocaleString()
              }
            }
          }
        }
      }
    })
  }

  getLineChartOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || ''
              if (label) {
                label += ': '
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y.toLocaleString()
              }
              return label
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return value.toLocaleString()
            }
          }
        }
      }
    }
  }
}
